/**
 * The moderator's audio session: a voice monitor over a whole cohort, or a
 * private chat with one player.
 *
 * Lifted out of match-moderation, which held fourteen pieces of state across
 * three subjects. These five are one subject, and they are bound by a rule the
 * component had to enforce in four places: only one kind of session can be open
 * at a time, so opening either has to close the other and wait for the teardown
 * before connecting. That interlock was spread through 200 lines of a file
 * about listing matches.
 *
 * It stays a hook rather than becoming a component: the markup is one screen,
 * and the windows it renders are already their own components.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { showErrorToast } from '../../components/toast/toast-store';
import { sayActionOutcome } from './action-outcome';
import { useAuthStore } from '../../services/authentication/auth-store';
import { useEndPrivateChat, useStartPrivateChat, useVoiceMonitorToken } from '../../api/moderator/moderation/hooks';
import { endPrivateChatAtSignOut } from '../../api/moderator/moderation/signout';
import { leaveAtSignOut } from '../../lib/session-end';
import { usePrivateVoiceChatStore } from './stores/private-voice-chat';
import type { PlayerDetails } from '../../api/moderator/moderation/types';
import type { CohortContext, PlayerPlace } from './moderation-view';

interface VoiceSessionInput {
  /** The cohort whose room the monitor would join. */
  selectedCohortContext: CohortContext | null;
  /** The player a private chat would be with, as their details read them: the match they are in among them. */
  selectedPlayer: PlayerDetails | null;
  /** Where the list showed the player as their card opened, which the private room is named by. */
  selectedPlayerContext: PlayerPlace | null;
}

// A moderator who signs out mid-call ends the call first, while their token
// works, as closing its window does: the player's match tells the player it
// is over, where the sign-out left them alone in a silent call.
leaveAtSignOut(async () => {
  const { playerCustomId, matchId } = usePrivateVoiceChatStore.getState();
  if (!playerCustomId || !matchId) return;
  await endPrivateChatAtSignOut({ playerCustomId, matchId, reason: 'ended' });
});

export function useVoiceSession({
  selectedCohortContext,
  selectedPlayer,
  selectedPlayerContext,
}: VoiceSessionInput) {
  const { t } = useTranslation();
  const currentUser = useAuthStore((state) => state.user);

  // Voice monitor state (combined voice chat + transcription)
  const [showVoiceMonitor, setShowVoiceMonitor] = useState(false);
  const [voiceMonitorConnection, setVoiceMonitorConnection] = useState<{
    token: string;
    serverUrl: string;
    worldName: string;
    cohortName: string;
  } | null>(null);
  const [isConnectingVoice, setIsConnectingVoice] = useState(false);

  // Private voice chat state
  const [showPrivateVoiceChat, setShowPrivateVoiceChat] = useState(false);
  const [isStartingPrivateChat, setIsStartingPrivateChat] = useState(false);
  const setPrivateChatConnectionInfo = usePrivateVoiceChatStore((state) => state.setConnectionInfo);
  const disconnectPrivateChat = usePrivateVoiceChatStore((state) => state.disconnect);
  const startPrivateChat = useStartPrivateChat();
  const endPrivateChat = useEndPrivateChat();
  const voiceMonitorToken = useVoiceMonitorToken();
  const handleOpenVoiceMonitor = async () => {
    if (!selectedCohortContext || !currentUser) {
      console.error('[VoiceSession] Cannot open voice monitor: missing context or user');
      return;
    }

    const { cohort, worldId, matchId } = selectedCohortContext;
    
    try {
      setIsConnectingVoice(true);

      // If private voice chat is open, close it first
      if (showPrivateVoiceChat) {
        await handleClosePrivateChat();
        // Wait a bit for cleanup
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // If voice monitor is already open, close it first
      if (showVoiceMonitor) {
        setShowVoiceMonitor(false);
        setVoiceMonitorConnection(null);
        // Wait a bit for cleanup
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // The backend and authoritative match own the monitor identity, room and grants.
      const tokenResponse = await voiceMonitorToken.mutateAsync({ matchId, cohortId: cohort.id });
      
      // Set connection info and show window
      setVoiceMonitorConnection({
        token: tokenResponse.token,
        serverUrl: tokenResponse.serverUrl,
        worldName: worldId, // worldId contains the world name
        cohortName: cohort.name,
      });
      
      setShowVoiceMonitor(true);
    } catch (error) {
      console.error('[VoiceSession] Failed to open voice monitor:', error);
      showErrorToast(t('voiceMonitor.connectionError'), error instanceof Error ? error.message : undefined);
    } finally {
      setIsConnectingVoice(false);
    }
  };

  // The transcript goes with the window (voice-monitor-window.tsx), however it closes.
  const handleCloseVoiceMonitor = () => {
    setShowVoiceMonitor(false);
    setVoiceMonitorConnection(null);
  };

  const handleStartPrivateChat = async () => {
    if (!selectedPlayer?.customId || !selectedPlayerContext || !currentUser) {
      console.error('[VoiceSession] Cannot start private chat: missing player or user');
      return;
    }

    // The player's match, as their details read it — where this searched the
    // list's last poll for the player, and found none in a match that poll had
    // found too busy to read. A chat that cannot start is said, as a ban's or
    // a resolved hand's failure is.
    const matchId = selectedPlayer.currentMatchId;
    if (!matchId) {
      showErrorToast(t('privateVoiceChat.startError'), t('matchModeration.playerNotInMatch'));
      return;
    }

    // If private voice chat is already open, close it first
    if (showPrivateVoiceChat) {
      await handleClosePrivateChat();
      // Wait a bit for cleanup
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    try {
      setIsStartingPrivateChat(true);

      // Close team voice monitor if open
      if (showVoiceMonitor) {
        handleCloseVoiceMonitor();
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Ask the player's match to invite them. A busy match invites them
      // once it is free: the moderator's room opens now, and the screen says
      // the invitation waits. One not started says why, and opens nothing.
      const response = await startPrivateChat.mutateAsync({
        playerCustomId: selectedPlayer.customId,
        gameId: selectedPlayerContext.gameId,
        worldId: selectedPlayerContext.worldName,
        cohortId: selectedPlayerContext.cohortId,
        matchId,
      });
      const outcome = sayActionOutcome(t, response, {
        queued: 'privateVoiceChat.startQueued',
        failed: 'privateVoiceChat.startError',
      });
      if (outcome === 'failed') return;

      // Store connection info in Zustand (use saved position if available)
      setPrivateChatConnectionInfo(
        response.server_url,
        response.moderator_token,
        response.room_name,
        selectedPlayer.customId,
        selectedPlayer.name,
        matchId,
        selectedPlayerContext.worldName,
        selectedPlayerContext.cohortName
      );

      // Open private voice chat window
      setShowPrivateVoiceChat(true);

      // Keep player modal open so moderator can see details while in private chat
    } catch (error) {
      console.error('[VoiceSession] Failed to start private chat:', error);
      showErrorToast(t('privateVoiceChat.startError'), error instanceof Error ? error.message : undefined);
    } finally {
      setIsStartingPrivateChat(false);
    }
  };

  const handleClosePrivateChat = async () => {
    if (!currentUser) return;

    const state = usePrivateVoiceChatStore.getState();
    const playerCustomId = state.playerCustomId;
    const matchId = state.matchId;
    
    if (playerCustomId && matchId) {
      try {
        // Tell the player's match the chat has ended. A busy match tells the
        // player once it is free; one not told is said.
        const response = await endPrivateChat.mutateAsync({
          playerCustomId,
          matchId,
          reason: 'ended',
        });
        sayActionOutcome(t, response, { queued: 'privateVoiceChat.endQueued', failed: 'privateVoiceChat.endError' });
      } catch (error) {
        console.error('[VoiceSession] Failed to end private chat:', error);
        showErrorToast(t('privateVoiceChat.endError'), error instanceof Error ? error.message : undefined);
      }
    }

    // Close window and disconnect
    setShowPrivateVoiceChat(false);
    disconnectPrivateChat();
  };
  return {
    showVoiceMonitor,
    voiceMonitorConnection,
    isConnectingVoice,
    showPrivateVoiceChat,
    isStartingPrivateChat,
    openVoiceMonitor: handleOpenVoiceMonitor,
    closeVoiceMonitor: handleCloseVoiceMonitor,
    startPrivateChat: handleStartPrivateChat,
    closePrivateChat: handleClosePrivateChat,
  };
}
